import { ComponentRef, Injectable } from '@angular/core';
import { ToastComponent, ToastModel } from '@syncfusion/ej2-angular-notifications';
import { DynamicComponentService } from '../dynamic-component/dynamic-component.service';
import { ToastRuntimeHostComponent } from './toast-runtime-host.component';

export interface AppToastOptions {
  title?: string;
  timeout?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toast: ToastComponent | null = null;
  private runtimeHostRef: ComponentRef<ToastRuntimeHostComponent> | null = null;
  private timeout: number = 8000;

  public toasts: { [key: string]: Object }[] = [
    { title: 'Warning!', content: 'There was a problem with your network connection.', cssClass: 'e-toast-warning', icon: 'toast-icons fas fa-exclamation-triangle' },
    { title: 'Success!', content: 'Your message has been sent successfully.', cssClass: 'e-toast-success', icon: 'toast-icons fas fa-check-circle' },
    { title: 'Error', content: 'A problem has been occurred while submitting your data.', cssClass: 'e-toast-danger', icon: 'toast-icons fas fa-exclamation-triangle' },
    { title: 'Information!', content: 'Please read the comments carefully.', cssClass: 'e-toast-info', icon: 'fas fa-info-circle toast-icons' },
    { title: 'No Permissions!', content: 'There was a problem.', cssClass: 'e-toast-warning', icon: 'toast-icons fad fa-lock' },
  ];

  constructor(private dynamicComponentService: DynamicComponentService) { }

  success(content: string = '', options?: AppToastOptions): void {
    this.showSuccess(content, options?.title ?? 'Sucesso!', options?.timeout);
  }

  warning(content: string = '', options?: AppToastOptions): void {
    this.showWarning(content, options?.title ?? 'Ops, ocorreu um problema!', options?.timeout);
  }

  error(content: string = '', options?: AppToastOptions): void {
    this.showError(content, options?.title ?? 'Ops, ocorreu um problema!', options?.timeout);
  }

  info(content: string = '', options?: AppToastOptions): void {
    this.showInfo(content, options?.title ?? 'Atenção!', options?.timeout);
  }

  public showAuthRefreshError(content: string = '', title: string = 'Ops, ocorreu um problema!') {
    let toastModel: ToastModel = this.toasts[2];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = this.timeout;

    this.showAuthToast(toastModel);
  }

  public showWarning(content: string = '', title: string = 'Ops, ocorreu um problema!', timeout?: number) {
    let toastModel: ToastModel = this.toasts[0];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = timeout ?? this.timeout;

    this.showToast(toastModel);
  }

  public showError(content: string = '', title: string = 'Ops, ocorreu um problema!', timeout?: number) {
    let toastModel: ToastModel = this.toasts[2];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = timeout ?? this.timeout;

    this.showToast(toastModel);
  }

  public showInfo(content: string = '', title: string = 'Atenção!', timeout?: number) {
    let toastModel: ToastModel = this.toasts[3];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = timeout ?? this.timeout;

    this.showToast(toastModel);
  }

  public showSuccess(content: string = '', title: string = 'Sucesso!', timeout?: number) {
    let toastModel: ToastModel = this.toasts[1];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = timeout ?? this.timeout;

    this.showToast(toastModel);
  }

  private showToast(toastModel: ToastModel) {
    if (this.toast) {
      this.toast.show(toastModel);
      return;
    }

    const runtimeHost = this.ensureRuntimeHost();
    runtimeHost?.instance.show(toastModel);
  }

  public showNoPermission(content: string = 'Desculpe, sem permissão para acessar este recurso', title: string = 'Sem Permissão!') {
    let toastModel: ToastModel = this.toasts[4];

    toastModel.title = title;
    toastModel.content = content;
    toastModel.timeOut = this.timeout;

    this.showToast(toastModel);
  }

  private showAuthToast(toastModel: ToastModel) {
    if (this.toast) {
      this.toast.hide('All');
      this.toast.show(toastModel);
      return;
    }

    const runtimeHost = this.ensureRuntimeHost();
    runtimeHost?.instance.hideAll();
    runtimeHost?.instance.show(toastModel);
  }

  private ensureRuntimeHost(): ComponentRef<ToastRuntimeHostComponent> | null {
    if (this.runtimeHostRef) {
      return this.runtimeHostRef;
    }

    this.runtimeHostRef = this.dynamicComponentService.appendComponentToBody(ToastRuntimeHostComponent);
    return this.runtimeHostRef;
  }
}
